import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useResendVerificationEmail } from '../hooks/useResendVerificationEmail';

// Configuration
const EMAIL_RESEND_COOLDOWN_SECONDS =
  parseInt(process.env.REACT_APP_EMAIL_RESEND_COOLDOWN || '60');

const EmailVerificationPending: React.FC = () => {
  const location = useLocation();

  // Get email from location state (passed from Register page)
  const email = location.state?.email || '';

  const { resendEmail, isResending, resendSuccess } = useResendVerificationEmail({
    showSuccessState: true
  });

  // Email resend cooldown state
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);

  // Timer effect for countdown
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Handle resend with cooldown
  const handleResend = async () => {
    if (!canResend || !email) return;

    // Start cooldown before sending (optimistic update)
    setCanResend(false);
    setCountdown(EMAIL_RESEND_COOLDOWN_SECONDS);

    // Send email
    try {
      await resendEmail(email);
    } catch {
      // On error, reset cooldown so user can retry
      setCanResend(true);
      setCountdown(0);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-primary-100">
            <EnvelopeIcon className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Check your email
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            We've sent a verification link to
          </p>
          {email && (
            <p className="mt-1 text-sm font-medium text-gray-900">
              {email}
            </p>
          )}
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Next steps
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ol className="list-decimal list-inside space-y-1">
                  <li>Check your inbox for an email from Fawayo</li>
                  <li>Click the verification link in the email</li>
                  <li>You'll be redirected back to login</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {resendSuccess && (
          <div className="rounded-md bg-green-50 border border-green-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Verification email sent! Please check your inbox.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={isResending || !canResend || !email}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                  Sending...
                </span>
              ) : !canResend ? (
                <span>Resend in {countdown}s</span>
              ) : (
                "Didn't receive the email? Resend"
              )}
            </button>
          </div>

          <div className="text-center text-sm text-gray-600">
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-700"
            >
              Back to login
            </Link>
          </div>
        </div>

        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-md p-4">
          <p className="text-xs text-gray-500">
            <strong>Tip:</strong> If you don't see the email, check your spam folder.
            The email will come from noreply@fawayo.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPending;
